import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { schedulingApi } from "../../services/schedulingApi";
import { formatApiErrorDetail } from "../../lib/api";
import { usePermissions } from "../../hooks/usePermissions";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { StoreSelect } from "../../components/StoreSelect";
import { useLocalization } from "../../hooks/useLocalization";

const EMPTY_FORM = { code: "", name_en: "", name_ja: "", start_time: "09:00", end_time: "17:00", crosses_midnight: false, default_break_minutes: 0, store_location: "" };

export default function ShiftTemplatesPage() {
  const { t } = useLocalization();
  const { canManageShiftTemplates, canViewScheduling } = usePermissions();
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");

  const load = () => schedulingApi.listTemplates().then((r) => setTemplates(r.data)).catch(() => {});

  useEffect(() => {
    if (!canViewScheduling) return;
    load();
  }, [canViewScheduling]);

  if (!canViewScheduling) return <p className="text-sm text-gray-500" data-testid="shift-templates-access-denied">{t("common.accessDenied")}</p>;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await schedulingApi.createTemplate(form);
      toast.success(t("mgmtScheduling.templateCreated"));
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleActive = async (tpl) => {
    try {
      await schedulingApi.updateTemplate(tpl.id, { active: !tpl.active });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="shift-templates-page">
      <PageHelpBanner pageKey="shift-templates" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtScheduling.templatesHelp") }} />
      </PageHelpBanner>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtScheduling.templatesTitle")}</h2>
          <p className="text-sm text-gray-500">{t("mgmtScheduling.templatesSubtitle")}</p>
        </div>
        {canManageShiftTemplates && (
          <button data-testid="new-shift-template-button" onClick={() => setShowForm((s) => !s)} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
            <Plus size={15} /> {t("mgmtScheduling.newTemplate")}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-3" data-testid="shift-template-form">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="template-code-input" required placeholder={t("mgmtScheduling.codePlaceholder")} value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <StoreSelect testId="template-store-input" value={form.store_location} onChange={(v) => setForm((f) => ({ ...f, store_location: v }))} allowEmpty emptyLabel={t("mgmtScheduling.everyStoreLabel")} className="px-3 py-2" />
            <input data-testid="template-name-en-input" required placeholder={t("mgmtScheduling.nameEnPlaceholder")} value={form.name_en} onChange={(e) => setForm((f) => ({ ...f, name_en: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <input data-testid="template-name-ja-input" required placeholder={t("mgmtScheduling.nameJaPlaceholder")} value={form.name_ja} onChange={(e) => setForm((f) => ({ ...f, name_ja: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <input data-testid="template-start-time-input" required type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <input data-testid="template-end-time-input" required type="time" value={form.end_time} onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <input data-testid="template-break-input" type="number" min="0" placeholder={t("mgmtScheduling.breakMinutesPlaceholder")} value={form.default_break_minutes} onChange={(e) => setForm((f) => ({ ...f, default_break_minutes: Number(e.target.value) }))} className="px-3 py-2 rounded-lg border border-gray-300 text-sm" />
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input data-testid="template-crosses-midnight-checkbox" type="checkbox" checked={form.crosses_midnight} onChange={(e) => setForm((f) => ({ ...f, crosses_midnight: e.target.checked }))} />
              {t("mgmtScheduling.crossesMidnight")}
            </label>
          </div>
          {error && <p className="text-sm text-red-500" data-testid="template-form-error">{error}</p>}
          <button data-testid="submit-shift-template-button" type="submit" className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">{t("mgmtScheduling.createTemplate")}</button>
        </form>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colCode")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colName")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colHours")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colBreak")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colStore")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtScheduling.colActive")}</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((tpl) => (
              <tr key={tpl.id} className="border-t border-gray-100" data-testid={`template-row-${tpl.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{tpl.code}</td>
                <td className="px-4 py-3">{tpl.name_en} / {tpl.name_ja}</td>
                <td className="px-4 py-3">{tpl.start_time} - {tpl.end_time}{tpl.crosses_midnight && <span className="text-xs text-amber-600 ml-1">({t("mgmtScheduling.crossMidnightTag")})</span>}</td>
                <td className="px-4 py-3">{tpl.default_break_minutes}{t("mgmtScheduling.minutesAbbrev")}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{tpl.store_location || t("mgmtScheduling.everyStoreLabel")}</td>
                <td className="px-4 py-3">
                  {canManageShiftTemplates ? (
                    <button data-testid={`toggle-template-active-${tpl.id}`} onClick={() => toggleActive(tpl)} className={`text-xs font-semibold px-2 py-1 rounded-full ${tpl.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                      {tpl.active ? t("mgmtScheduling.active") : t("mgmtScheduling.inactive")}
                    </button>
                  ) : (
                    <span className="text-xs">{tpl.active ? t("mgmtScheduling.active") : t("mgmtScheduling.inactive")}</span>
                  )}
                </td>
              </tr>
            ))}
            {templates.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">{t("mgmtScheduling.noTemplates")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
